/* ============================================================
   FHAUS 3D — scroll-driven WebGL product storytelling
   - Lazy-loads Three.js from Shopify theme assets only when the section nears
     the viewport (zero cost for the rest of the site).
   - Organic shader blob in brand colours + floating product
     image that crossfades per story step + ambient particles.
   - Scroll progress drives camera, morph and accent colour.
   - Pointer parallax on fine pointers.
   - Graceful fallback (static layout) when WebGL is missing,
     the module is unavailable, or prefers-reduced-motion is set.
   ============================================================ */
(function () {
  'use strict';
  if (window.__fh3dLoaded) return;
  window.__fh3dLoaded = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function supportsWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }

  function initSection(section) {
    if (section.__fh3d) return;
    section.__fh3d = true;

    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-fh-3d-step]'));
    section.__fhStep = 0;

    // Step activation drives both the card highlight and the 3D scene
    var stepIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        steps.forEach(function (s) { s.classList.remove('is-active'); });
        e.target.classList.add('is-active');
        section.__fhStep = steps.indexOf(e.target);
      });
    }, { rootMargin: '-45% 0px -45% 0px' });
    steps.forEach(function (s) { stepIO.observe(s); });

    if (reduce || !supportsWebGL()) {
      section.classList.add('fh3d-fallback');
      return;
    }

    var armed = false;
    var nearIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && !armed) {
          armed = true;
          nearIO.disconnect();
          var threeUrl = section.getAttribute('data-three-url');
          if (!threeUrl) {
            section.classList.add('fh3d-fallback');
            return;
          }
          import(threeUrl)
            .then(function (THREE) { start(section, THREE); })
            .catch(function () { section.classList.add('fh3d-fallback'); });
        }
      });
    }, { rootMargin: '600px' });
    nearIO.observe(section);
  }

  function start(section, THREE) {
    var wrap = section.querySelector('[data-fh-3d-canvas]');
    if (!wrap) return;
    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-fh-3d-step]'));
    var accents = steps.map(function (el) { return el.getAttribute('data-accent') || '#c49359'; });
    var images = steps.map(function (el) { return el.getAttribute('data-image') || ''; });

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch (e) {
      section.classList.add('fh3d-fallback');
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    wrap.appendChild(renderer.domElement);
    section.classList.add('fh3d-live');

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(35, 1, 0.1, 60);
    camera.position.set(0, 0, 7);

    /* ---- organic shader blob ------------------------------------ */
    var simplex = [
      'vec3 mod289(vec3 x){return x - floor(x * (1.0/289.0)) * 289.0;}',
      'vec4 mod289(vec4 x){return x - floor(x * (1.0/289.0)) * 289.0;}',
      'vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}',
      'vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}',
      'float snoise(vec3 v){',
      '  const vec2 C = vec2(1.0/6.0, 1.0/3.0);',
      '  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);',
      '  vec3 i  = floor(v + dot(v, C.yyy));',
      '  vec3 x0 = v - i + dot(i, C.xxx);',
      '  vec3 g = step(x0.yzx, x0.xyz);',
      '  vec3 l = 1.0 - g;',
      '  vec3 i1 = min(g.xyz, l.zxy);',
      '  vec3 i2 = max(g.xyz, l.zxy);',
      '  vec3 x1 = x0 - i1 + C.xxx;',
      '  vec3 x2 = x0 - i2 + C.yyy;',
      '  vec3 x3 = x0 - D.yyy;',
      '  i = mod289(i);',
      '  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));',
      '  float n_ = 0.142857142857;',
      '  vec3 ns = n_ * D.wyz - D.xzx;',
      '  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);',
      '  vec4 x_ = floor(j * ns.z);',
      '  vec4 y_ = floor(j - 7.0 * x_);',
      '  vec4 x = x_ * ns.x + ns.yyyy;',
      '  vec4 y = y_ * ns.x + ns.yyyy;',
      '  vec4 h = 1.0 - abs(x) - abs(y);',
      '  vec4 b0 = vec4(x.xy, y.xy);',
      '  vec4 b1 = vec4(x.zw, y.zw);',
      '  vec4 s0 = floor(b0)*2.0 + 1.0;',
      '  vec4 s1 = floor(b1)*2.0 + 1.0;',
      '  vec4 sh = -step(h, vec4(0.0));',
      '  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;',
      '  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;',
      '  vec3 p0 = vec3(a0.xy, h.x);',
      '  vec3 p1 = vec3(a0.zw, h.y);',
      '  vec3 p2 = vec3(a1.xy, h.z);',
      '  vec3 p3 = vec3(a1.zw, h.w);',
      '  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));',
      '  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;',
      '  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);',
      '  m = m * m;',
      '  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));',
      '}'
    ].join('\n');

    var blobUniforms = {
      uTime: { value: 0 },
      uAmp: { value: 0.42 },
      uColA: { value: new THREE.Color('#1d4630') },
      uColB: { value: new THREE.Color('#a8b397') },
      uAccent: { value: new THREE.Color(accents[0]) },
      uScroll: { value: 0 }
    };
    var blobMat = new THREE.ShaderMaterial({
      uniforms: blobUniforms,
      vertexShader: simplex + [
        'uniform float uTime; uniform float uAmp; uniform float uScroll;',
        'varying float vDisp; varying vec3 vNormalW; varying vec3 vViewW;',
        'void main(){',
        '  float n = snoise(normal * (1.35 + uScroll * 0.5) + vec3(uTime * 0.18));',
        '  vDisp = n;',
        '  vec3 displaced = position + normal * n * uAmp;',
        '  vec4 world = modelMatrix * vec4(displaced, 1.0);',
        '  vNormalW = normalize(mat3(modelMatrix) * normal);',
        '  vViewW = normalize(cameraPosition - world.xyz);',
        '  gl_Position = projectionMatrix * viewMatrix * world;',
        '}'
      ].join('\n'),
      fragmentShader: [
        'uniform vec3 uColA; uniform vec3 uColB; uniform vec3 uAccent;',
        'varying float vDisp; varying vec3 vNormalW; varying vec3 vViewW;',
        'void main(){',
        '  float fres = pow(1.0 - max(dot(vNormalW, vViewW), 0.0), 2.2);',
        '  vec3 base = mix(uColA, uColB, smoothstep(-0.7, 0.9, vDisp));',
        '  vec3 col = mix(base, uAccent, fres * 0.85);',
        '  col += uAccent * fres * 0.18;',
        '  gl_FragColor = vec4(col, 1.0);',
        '}'
      ].join('\n')
    });
    var blob = new THREE.Mesh(new THREE.IcosahedronGeometry(1.55, 48), blobMat);
    scene.add(blob);

    /* ---- ambient particle drift ---------------------------------- */
    var pCount = 140;
    var pGeo = new THREE.BufferGeometry();
    var pPos = new Float32Array(pCount * 3);
    for (var i = 0; i < pCount; i++) {
      pPos[i * 3] = (Math.random() - 0.5) * 10;
      pPos[i * 3 + 1] = (Math.random() - 0.5) * 7;
      pPos[i * 3 + 2] = (Math.random() - 0.5) * 4 - 1;
    }
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    var dot = document.createElement('canvas');
    dot.width = dot.height = 64;
    var dctx = dot.getContext('2d');
    var grad = dctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(196,147,89,0.9)');
    grad.addColorStop(1, 'rgba(196,147,89,0)');
    dctx.fillStyle = grad;
    dctx.fillRect(0, 0, 64, 64);
    var particles = new THREE.Points(pGeo, new THREE.PointsMaterial({
      size: 0.16,
      map: new THREE.CanvasTexture(dot),
      transparent: true,
      opacity: 0.5,
      depthWrite: false
    }));
    scene.add(particles);

    /* ---- floating product image (crossfades per step) ------------ */
    var texLoader = new THREE.TextureLoader();
    texLoader.setCrossOrigin('anonymous');
    var productGroup = new THREE.Group();
    scene.add(productGroup);

    function makeCard() {
      var mat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, toneMapped: false });
      var mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
      mesh.visible = false;
      productGroup.add(mesh);
      return mesh;
    }
    var cardA = makeCard(), cardB = makeCard();
    var frontCard = cardA, backCard = cardB;
    var fade = { active: false, t: 0 };

    function setCardTexture(mesh, url, cb) {
      texLoader.load(url, function (tex) {
        tex.colorSpace = THREE.SRGBColorSpace;
        var aspect = tex.image && tex.image.height ? tex.image.width / tex.image.height : 1;
        var h = 2.0;
        mesh.geometry.dispose();
        mesh.geometry = new THREE.PlaneGeometry(h * aspect, h);
        mesh.material.map = tex;
        mesh.material.needsUpdate = true;
        mesh.visible = true;
        if (cb) cb();
      }, undefined, function () { /* texture blocked/missing — blob carries the scene */ });
    }

    var shownStep = -1;
    function showStep(idx) {
      if (idx === shownStep) return;
      shownStep = idx;
      targetAccent.set(accents[idx] || accents[0]);
      if (!images[idx]) return;
      if (!frontCard.material.map) {
        setCardTexture(frontCard, images[idx]);
        frontCard.material.opacity = 0;
        fade = { active: true, t: 0, inMesh: frontCard, outMesh: null };
        return;
      }
      var inMesh = backCard, outMesh = frontCard;
      setCardTexture(inMesh, images[idx], function () {
        inMesh.material.opacity = 0;
        fade = { active: true, t: 0, inMesh: inMesh, outMesh: outMesh };
        frontCard = inMesh;
        backCard = outMesh;
      });
    }

    var targetAccent = new THREE.Color(accents[0]);

    /* ---- responsive layout --------------------------------------- */
    function layout() {
      var w = wrap.clientWidth, h = wrap.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      var narrow = camera.aspect < 0.9;
      blob.position.set(narrow ? 0 : -1.35, narrow ? 0.6 : 0.05, 0);
      productGroup.position.set(narrow ? 0 : -1.05, narrow ? -0.55 : -0.35, 1.15);
      productGroup.scale.setScalar(narrow ? 0.72 : 0.9);
    }
    layout();
    if (window.ResizeObserver) new ResizeObserver(layout).observe(wrap);
    else window.addEventListener('resize', layout, { passive: true });

    /* ---- scroll + pointer drivers --------------------------------- */
    var progress = 0;
    function onScroll() {
      var r = section.getBoundingClientRect();
      var total = r.height - window.innerHeight;
      progress = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    var px = 0, py = 0;
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      window.addEventListener('pointermove', function (e) {
        px = (e.clientX / window.innerWidth - 0.5) * 2;
        py = (e.clientY / window.innerHeight - 0.5) * 2;
      }, { passive: true });
    }

    /* ---- render loop (paused offscreen / hidden tab) -------------- */
    var visible = false;
    var visIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { visible = e.isIntersecting; });
    });
    visIO.observe(section);

    var clock = new THREE.Clock();
    var smooth = 0;
    renderer.setAnimationLoop(function () {
      if (!visible || document.hidden) return;
      var dt = Math.min(clock.getDelta() || 0.016, 0.05);
      var t = clock.elapsedTime;

      smooth += (progress - smooth) * 0.06;
      blobUniforms.uTime.value = t;
      blobUniforms.uScroll.value = smooth;
      blobUniforms.uAccent.value.lerp(targetAccent, 0.04);

      showStep(section.__fhStep || 0);

      blob.rotation.y = t * 0.07 + smooth * 1.4;
      blob.rotation.x = Math.sin(t * 0.1) * 0.12 + smooth * 0.3;

      productGroup.rotation.z = Math.sin(t * 0.5) * 0.03;
      productGroup.position.y += (Math.sin(t * 0.8) * 0.0006);
      productGroup.rotation.y = px * 0.12;

      particles.rotation.y = t * 0.015;

      if (fade.active) {
        fade.t = Math.min(1, fade.t + dt * 1.6);
        var e2 = fade.t * fade.t * (3 - 2 * fade.t);
        if (fade.inMesh) fade.inMesh.material.opacity = e2;
        if (fade.outMesh) fade.outMesh.material.opacity = 1 - e2;
        if (fade.t >= 1) {
          if (fade.outMesh) fade.outMesh.visible = false;
          fade.active = false;
        }
      }

      camera.position.x += (px * 0.35 - camera.position.x) * 0.04;
      camera.position.y += (-py * 0.25 - smooth * 0.5 - camera.position.y) * 0.04;
      camera.lookAt(blob.position.x * 0.6, 0, 0);
      renderer.render(scene, camera);
    });
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-fh-3d]').forEach(initSection);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
